import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/lib/supabase";
import { useAuth } from "@/context/AuthContext";
import { useParams } from "react-router-dom";
import { PageHeader } from "@/components/base/PageHeader";
import { LoadingState } from "@/components/base/LoadingState";
import { classifyBcMode, type BcHonestMode } from "@/lib/providers/bigcommerce-adapter";
import { isAdmin } from "@/lib/permissions";

function HonestModeBadge({ mode }: { mode: BcHonestMode }) {
  const styles: Record<BcHonestMode, string> = {
    NOT_CONFIGURED: "bg-red-50 text-red-700 border border-red-200",
    DRY_RUN: "bg-amber-50 text-amber-700 border border-amber-200",
    LIVE: "bg-emerald-50 text-emerald-700 border border-emerald-200",
  };
  return (
    <span className={`text-xs px-2 py-0.5 rounded-full font-bold ${styles[mode]}`}>
      {mode === "NOT_CONFIGURED" ? "NOT CONFIGURED" : mode}
    </span>
  );
}

export default function BigCommerceConnectorConfig() {
  const { connectorId } = useParams();
  const { org, role } = useAuth();
  const queryClient = useQueryClient();
  const isAdminUser = isAdmin(role);

  const [testBusy, setTestBusy] = useState(false);
  const [testResult, setTestResult] = useState<Record<string, unknown> | null>(null);
  const [saveBusy, setSaveBusy] = useState(false);
  const [saveError, setSaveError] = useState("");
  const [rotateBusy, setRotateBusy] = useState(false);
  const [disconnectBusy, setDisconnectBusy] = useState(false);
  const [credMessage, setCredMessage] = useState("");
  const [form, setForm] = useState({ store_hash: "", api_path: "", auth_token: "", currency: "", channel_id: "", storefront_origin: "" });

  const { data: connector, isLoading: connLoading } = useQuery({
    queryKey: ["bc-connector", org?.id, connectorId],
    enabled: !!org?.id && !!connectorId,
    queryFn: async () => {
      const { data } = await supabase.from("connectors").select("*").eq("id", connectorId).eq("org_id", org!.id).maybeSingle();
      return data;
    },
  });

  const { data: config, isLoading: configLoading } = useQuery({
    queryKey: ["bc-config", org?.id, connectorId],
    enabled: !!org?.id && !!connectorId,
    queryFn: async () => {
      const { data } = await supabase.from("connector_bigcommerce_config").select("*").eq("connector_id", connectorId).maybeSingle();
      return data;
    },
  });

  const { data: dashboard } = useQuery({
    queryKey: ["bc-dashboard", connectorId],
    enabled: !!connectorId,
    queryFn: async () => {
      const { data } = await supabase.rpc("get_bigcommerce_store_dashboard", { p_connector_id: connectorId });
      return data;
    },
  });

  const { data: health } = useQuery({
    queryKey: ["bc-health", connectorId],
    enabled: !!connectorId,
    queryFn: async () => {
      const { data } = await supabase.from("bigcommerce_connection_health").select("*").eq("connector_id", connectorId).maybeSingle();
      return data;
    },
  });

  const honestMode = classifyBcMode(config);

  const handleSave = async () => {
    if (!connectorId) return;
    setSaveBusy(true);
    setSaveError("");
    setCredMessage("");
    try {
      if (isAdminUser && form.auth_token.trim() !== "") {
        // Admin: persist the token securely via the credential-manage edge function.
        // The plaintext is encrypted server-side and never stored in frontend config.
        const { data, error } = await supabase.functions.invoke("bigcommerce-credential-manage", {
          body: {
            action: "save",
            connector_id: connectorId,
            store_hash: form.store_hash || config?.store_hash || null,
            api_path: form.api_path || config?.api_path || null,
            currency: form.currency || config?.currency || "USD",
            channel_id: form.channel_id || config?.channel_id || null,
            access_token: form.auth_token,
          },
        });
        if (error || !data?.success) {
          throw new Error((data?.error as string) || error?.message || "Failed to save credentials");
        }
        setCredMessage("Configuration saved.");
      } else {
        const { error } = await supabase.rpc("initialize_bigcommerce_connector_config", {
          p_connector_id: connectorId,
          p_store_hash: form.store_hash || config?.store_hash || null,
          p_api_path: form.api_path || config?.api_path || null,
          p_currency: form.currency || config?.currency || "USD",
          p_channel_id: form.channel_id || config?.channel_id || null,
        });
        if (error) throw error;
        setCredMessage("Configuration saved.");
      }

      queryClient.invalidateQueries({ queryKey: ["bc-config", org?.id, connectorId] });
      queryClient.invalidateQueries({ queryKey: ["bc-connector", org?.id, connectorId] });
      setForm({ store_hash: "", api_path: "", auth_token: "", currency: "", channel_id: "", storefront_origin: "" });
    } catch (err) {
      setSaveError((err as Error).message);
    }
    setSaveBusy(false);
  };

  const handleTest = async () => {
    if (!connectorId) return;
    setTestBusy(true);
    try {
      const res = await supabase.functions.invoke("bigcommerce-connection-test", {
        body: { connector_id: connectorId },
      });
      if (res.error && !res.data) {
        setTestResult({ success: false, error: res.error.message });
      } else {
        setTestResult(res.data || { success: false, error: "Unknown error" });
        if (res.data?.success) {
          queryClient.invalidateQueries({ queryKey: ["bc-connector", org?.id, connectorId] });
          queryClient.invalidateQueries({ queryKey: ["bc-config", org?.id, connectorId] });
          queryClient.invalidateQueries({ queryKey: ["bc-health", connectorId] });
        }
      }
    } catch (err) {
      setTestResult({ success: false, error: (err as Error).message });
    }
    setTestBusy(false);
  };

  const handleRotate = async () => {
    if (!connectorId || form.auth_token.trim() === "") {
      setSaveError("Enter a new API token to rotate.");
      return;
    }
    setRotateBusy(true);
    setSaveError("");
    setCredMessage("");
    try {
      const { data, error } = await supabase.functions.invoke("bigcommerce-credential-manage", {
        body: { action: "rotate", connector_id: connectorId, access_token: form.auth_token },
      });
      if (error || !data?.success) {
        throw new Error((data?.error as string) || error?.message || "Rotation failed");
      }
      setCredMessage("Token rotated.");
      setForm((f) => ({ ...f, auth_token: "" }));
      queryClient.invalidateQueries({ queryKey: ["bc-config", org?.id, connectorId] });
    } catch (err) {
      setSaveError((err as Error).message);
    }
    setRotateBusy(false);
  };

  const handleDisconnect = async () => {
    if (!connectorId) return;
    setDisconnectBusy(true);
    setSaveError("");
    setCredMessage("");
    try {
      const { data, error } = await supabase.functions.invoke("bigcommerce-credential-manage", {
        body: { action: "disconnect", connector_id: connectorId },
      });
      if (error || !data?.success) {
        throw new Error((data?.error as string) || error?.message || "Disconnect failed");
      }
      setCredMessage("Credentials removed.");
      queryClient.invalidateQueries({ queryKey: ["bc-config", org?.id, connectorId] });
      queryClient.invalidateQueries({ queryKey: ["bc-connector", org?.id, connectorId] });
    } catch (err) {
      setSaveError((err as Error).message);
    }
    setDisconnectBusy(false);
  };

  if (connLoading || configLoading) return <LoadingState />;

  if (!isAdminUser) {
    return (
      <div className="space-y-6">
        <PageHeader title={connector?.name || "BigCommerce Store"} backTo="/integrations" />
        <div className="px-4 py-3 bg-slate-50 border border-slate-200 rounded-lg text-sm text-slate-600">
          An organization administrator manages this connector. BigCommerce authorization is started from the Connectors page.
        </div>
      </div>
    );
  }

  const listings = dashboard?.listings || { published: 0, ready: 0, dirty: 0, error: 0, dead_letters: 0 };

  return (
    <div className="space-y-6">
      <PageHeader title={connector?.name || "BigCommerce Store"} backTo="/integrations">
        <HonestModeBadge mode={honestMode} />
      </PageHeader>

      {/* Mode Banner */}
      {honestMode === "NOT_CONFIGURED" && (
        <div className="px-4 py-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">
          <strong>Not Configured:</strong> Use Connect on the Connectors page for the normal BigCommerce authorization flow.
        </div>
      )}
      {honestMode === "DRY_RUN" && (
        <div className="px-4 py-3 bg-amber-50 border border-amber-200 rounded-lg text-sm text-amber-700">
          <strong>Dry Run Mode:</strong> Credentials configured but store not authenticated. You can validate listings, but no live API calls will be made.
        </div>
      )}
      {honestMode === "LIVE" && (
        <div className="px-4 py-3 bg-emerald-50 border border-emerald-200 rounded-lg text-sm text-emerald-700">
          <strong>Live Mode:</strong> Store connected. Publishing will make real API calls to BigCommerce.
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Configuration Panel */}
        <div className="lg:col-span-2 space-y-6">
          <div className="bg-white rounded-xl border border-slate-200 p-5">
            <h3 className="text-sm font-semibold text-slate-800">Advanced admin fallback</h3>
            <p className="text-xs text-slate-500 mt-1 mb-4">
              Manual store configuration is for recovery and legacy installations only. Normal merchant setup uses BigCommerce authorization from the Connectors page.
            </p>
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-medium text-slate-500 mb-1">Store Hash</label>
                  <input
                    type="text"
                    value={form.store_hash || config?.store_hash || ""}
                    onChange={(e) => setForm((f) => ({ ...f, store_hash: e.target.value }))}
                    placeholder="abc123def"
                    className="w-full px-3 py-2 bg-slate-50 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-slate-500 mb-1">API Path</label>
                  <input
                    type="text"
                    value={form.api_path || config?.api_path || ""}
                    onChange={(e) => setForm((f) => ({ ...f, api_path: e.target.value }))}
                    placeholder="/stores/abc123"
                    className="w-full px-3 py-2 bg-slate-50 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500"
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-medium text-slate-500 mb-1">Currency</label>
                  <select
                    value={form.currency || config?.currency || "USD"}
                    onChange={(e) => setForm((f) => ({ ...f, currency: e.target.value }))}
                    className="w-full px-3 py-2 bg-slate-50 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500"
                  >
                    <option value="USD">USD</option>
                    <option value="AUD">AUD</option>
                    <option value="GBP">GBP</option>
                    <option value="EUR">EUR</option>
                    <option value="CAD">CAD</option>
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-medium text-slate-500 mb-1">Channel ID (optional)</label>
                  <input
                    type="text"
                    value={form.channel_id || config?.channel_id || ""}
                    onChange={(e) => setForm((f) => ({ ...f, channel_id: e.target.value }))}
                    placeholder="1"
                    className="w-full px-3 py-2 bg-slate-50 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500"
                  />
                </div>
              </div>
              {isAdminUser && (
                <div>
                  <label className="block text-xs font-medium text-slate-500 mb-1">API Token (admin only)</label>
                  <input
                    type="password"
                    value={form.auth_token}
                    onChange={(e) => setForm((f) => ({ ...f, auth_token: e.target.value }))}
                    placeholder="Enter X-Auth-Token"
                    className="w-full px-3 py-2 bg-slate-50 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500"
                  />
                  <p className="text-xs text-slate-400 mt-1">Token is encrypted server-side. Never displayed after saving.</p>
                </div>
              )}
              <div>
                <label className="block text-xs font-medium text-slate-500 mb-1">Public storefront origin (HTTPS)</label>
                <input
                  type="url"
                  value={form.storefront_origin || config?.storefront_origin || ""}
                  onChange={(e) => setForm((f) => ({ ...f, storefront_origin: e.target.value }))}
                  placeholder="https://shop.example.com"
                  className="w-full px-3 py-2 bg-slate-50 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500"
                />
                <p className="text-xs text-slate-400 mt-1">
                  Used for customer “View live” links. Prefer the origin returned by Test Connection. HTTPS host only — no credentials or Admin URLs.
                </p>
                {isAdminUser && (
                  <button
                    type="button"
                    onClick={async () => {
                      if (!connectorId) return;
                      setSaveBusy(true);
                      setSaveError("");
                      try {
                        const { data, error } = await supabase.rpc("set_bigcommerce_storefront_origin", {
                          p_connector_id: connectorId,
                          p_origin: form.storefront_origin || config?.storefront_origin || "",
                        });
                        if (error) throw error;
                        if (data && typeof data === "object" && "success" in data && !(data as { success?: boolean }).success) {
                          throw new Error((data as { error?: string }).error || "Invalid HTTPS storefront origin");
                        }
                        setCredMessage("Storefront origin saved.");
                        queryClient.invalidateQueries({ queryKey: ["bc-config", org?.id, connectorId] });
                      } catch (err) {
                        setSaveError((err as Error).message);
                      }
                      setSaveBusy(false);
                    }}
                    disabled={saveBusy}
                    className="mt-2 px-3 py-1.5 bg-white border border-slate-200 hover:bg-slate-50 disabled:opacity-50 text-slate-700 text-xs font-medium rounded-lg whitespace-nowrap"
                  >
                    Save storefront origin
                  </button>
                )}
              </div>
            </div>

            {saveError && <div className="mt-3 px-3 py-2 bg-red-50 border border-red-100 rounded-lg text-sm text-red-700">{saveError}</div>}

            <div className="flex items-center gap-2 mt-4">
              <button
                onClick={handleSave}
                disabled={saveBusy}
                className="px-4 py-2 bg-emerald-600 hover:bg-emerald-700 disabled:opacity-60 text-white text-sm font-medium rounded-lg whitespace-nowrap"
              >
                {saveBusy ? "Saving..." : "Save Configuration"}
              </button>
              <button
                onClick={handleTest}
                disabled={testBusy || honestMode === "NOT_CONFIGURED"}
                className="px-4 py-2 bg-white border border-slate-200 hover:bg-slate-50 disabled:opacity-50 text-slate-700 text-sm font-medium rounded-lg whitespace-nowrap"
              >
                {testBusy ? "Testing..." : "Test Connection"}
              </button>
              {isAdminUser && (
                <>
                  <button
                    onClick={handleRotate}
                    disabled={rotateBusy}
                    className="px-4 py-2 bg-white border border-slate-200 hover:bg-slate-50 disabled:opacity-50 text-slate-700 text-sm font-medium rounded-lg whitespace-nowrap"
                  >
                    {rotateBusy ? "Rotating..." : "Rotate Token"}
                  </button>
                  <button
                    onClick={handleDisconnect}
                    disabled={disconnectBusy}
                    className="px-4 py-2 bg-white border border-red-200 hover:bg-red-50 disabled:opacity-50 text-red-600 text-sm font-medium rounded-lg whitespace-nowrap"
                  >
                    {disconnectBusy ? "Removing..." : "Disconnect"}
                  </button>
                </>
              )}
            </div>

            {credMessage && (
              <div className="mt-2 px-3 py-2 bg-emerald-50 border border-emerald-100 rounded-lg text-sm text-emerald-700">{credMessage}</div>
            )}

            {testResult && (
              <div className={`mt-3 px-3 py-2 rounded-lg text-sm ${testResult.success ? "bg-emerald-50 border border-emerald-100 text-emerald-700" : "bg-red-50 border border-red-100 text-red-700"}`}>
                {testResult.success ? "Connection successful." : `Connection failed: ${testResult.error}`}
                {testResult.success && typeof (testResult as { identity?: { storefront_origin?: string | null } }).identity?.storefront_origin === "string" ? (
                  <span className="block mt-1">Storefront: {(testResult as { identity: { storefront_origin: string } }).identity.storefront_origin}</span>
                ) : null}
              </div>
            )}
          </div>

          {/* Capabilities */}
          <div className="bg-white rounded-xl border border-slate-200 p-5">
            <h3 className="text-sm font-semibold text-slate-800 mb-3">Capabilities</h3>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
              {[
                { key: "products", label: "Products" },
                { key: "variants", label: "Variants" },
                { key: "options", label: "Options" },
                { key: "categories", label: "Categories" },
                { key: "brands", label: "Brands" },
                { key: "images", label: "Images" },
                { key: "inventory", label: "Inventory" },
                { key: "orders", label: "Orders" },
                { key: "shipments", label: "Shipments" },
                { key: "webhooks", label: "Webhooks" },
              ].map((cap) => (
                <div key={cap.key} className="flex items-center gap-2 text-sm">
                  <i className="ri-check-line text-emerald-600" />
                  <span className="text-slate-700">{cap.label}</span>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Dashboard Sidebar */}
        <div className="space-y-6">
          <div className="bg-white rounded-xl border border-slate-200 p-5">
            <h3 className="text-sm font-semibold text-slate-800 mb-3">Store Dashboard</h3>
            <div className="space-y-3 text-sm">
              <div className="flex justify-between">
                <span className="text-slate-500">Published</span>
                <span className="font-medium text-slate-800">{listings.published}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-slate-500">Ready</span>
                <span className="font-medium text-slate-800">{listings.ready}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-slate-500">Dirty</span>
                <span className="font-medium text-amber-700">{listings.dirty}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-slate-500">Errors</span>
                <span className="font-medium text-red-700">{listings.error}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-slate-500">Dead Letters</span>
                <span className="font-medium text-red-800">{listings.dead_letters}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-slate-500">Orders</span>
                <span className="font-medium text-slate-800">{dashboard?.orders?.total_imported || 0}</span>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-xl border border-slate-200 p-5">
            <h3 className="text-sm font-semibold text-slate-800 mb-3">Connection Health</h3>
            <div className="space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="text-slate-500">Last success</span>
                <span className="text-slate-700">{health?.last_api_success_at ? new Date(health.last_api_success_at).toLocaleDateString() : "Not reported"}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-slate-500">Consecutive failures</span>
                <span className={`font-medium ${!health ? "text-slate-500" : (health.consecutive_failures || 0) > 3 ? "text-red-700" : "text-slate-800"}`}>
                  {health ? String(health.consecutive_failures ?? 0) : "Not reported"}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-slate-500">API calls (24h)</span>
                <span className="text-slate-700">{health ? String(health.api_call_count_24h ?? 0) : "Not reported"}</span>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-xl border border-slate-200 p-5">
            <h3 className="text-sm font-semibold text-slate-800 mb-2">Setup Status</h3>
            <div className="space-y-2">
              {[
                { label: "App Credentials", state: honestMode !== "NOT_CONFIGURED" ? "ok" : "no" },
                {
                  label: "Store Authentication",
                  state: ["authorized", "connected"].includes(connector?.auth_status || config?.auth_status || "") ? "ok" : "no",
                },
                {
                  // Positive token health requires an actual health record with
                  // affirmative success evidence — never an absent row defaulting to zero.
                  label: "Token Health",
                  state: !health
                    ? "unknown"
                    : (health.consecutive_failures ?? 0) === 0 && Boolean(health.last_api_success_at)
                      ? "ok"
                      : "no",
                },
                {
                  label: "Categories",
                  state: dashboard?.config?.last_category_sync_at != null ? "ok" : "unknown",
                },
              ].map((item) => (
                <div key={item.label} className="flex items-center gap-2 text-sm">
                  <i
                    className={
                      item.state === "ok"
                        ? "ri-check-line text-emerald-600"
                        : item.state === "no"
                          ? "ri-close-line text-red-500"
                          : "ri-question-line text-slate-400"
                    }
                  />
                  <span className={item.state === "ok" ? "text-slate-700" : "text-slate-400"}>
                    {item.label}
                    {item.state === "unknown" ? " — not reported" : ""}
                  </span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
